import React, { useState, useEffect, useMemo } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, doc, writeBatch, getDocs, getDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { TrashIcon, UploadIcon } from '../../components/icons/Icons';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { sendNotificationEmail, newExpenseReportTemplate } from '../../firebase/notifications';
import { getFunctions, httpsCallable } from 'firebase/functions';

const allCurrencies = ["AED","AFN","ALL","AMD","ANG","AOA","ARS","AUD","AWG","AZN","BAM","BBD","BDT","BGN","BHD","BIF","BMD","BND","BOB","BRL","BSD","BTN","BWP","BYN","BZD","CAD","CDF","CHF","CLP","CNY","COP","CRC","CUP","CVE","CZK","DJF","DKK","DOP","DZD","EGP","ERN","ETB","EUR","FJD","FKP","FOK","GBP","GEL","GGP","GHS","GIP","GMD","GNF","GTQ","GYD","HKD","HNL","HRK","HTG","HUF","IDR","ILS","IMP","INR","IQD","IRR","ISK","JEP","JMD","JOD","JPY","KES","KGS","KHR","KID","KMF","KRW","KWD","KYD","KZT","LAK","LBP","LKR","LRD","LSL","LYD","MAD","MDL","MGA","MKD","MMK","MNT","MOP","MRU","MUR","MVR","MWK","MXN","MYR","MZN","NAD","NGN","NIO","NOK","NPR","NZD","OMR","PAB","PEN","PGK","PHP","PKR","PLN","PYG","QAR","RON","RSD","RUB","RWF","SAR","SBD","SCR","SDG","SEK","SGD","SHP","SLE","SLL","SOS","SRD","SSP","STN","SYP","SZL","THB","TJS","TMT","TND","TOP","TRY","TTD","TVD","TWD","TZS","UAH","UGX","USD","UYU","UZS","VES","VND","VUV","WST","XAF","XCD","XDR","XOF","XPF","YER","ZAR","ZMW","ZWL"];

const generateReportNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const randomSuffix = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    return `ER-${year}${month}${day}-${randomSuffix}`;
};

export default function NewExpenseReport({ userProfile, showModal, appId, onCancel, initialData = null }) {
    const isEditMode = initialData !== null;

    const [reportName, setReportName] = useState('');
    const [entity, setEntity] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [items, setItems] = useState([]);
    const [deletedItemIds, setDeletedItemIds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [ratesLoading, setRatesLoading] = useState(true);

    useEffect(() => {
        if (!appId) return;
        const categoriesRef = collection(db, `/artifacts/${appId}/public/data/expenseCategories`);
        const q = query(categoriesRef, orderBy('name'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedCategories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(fetchedCategories);
            setCategoriesLoading(false);
        });
        return () => unsubscribe();
    }, [appId]);
    
    useEffect(() => {
        if (isEditMode && initialData) {
            setReportName(initialData.reportName);
            setEntity(initialData.entity);
            const fetchItems = async () => {
                const itemsRef = collection(db, `/artifacts/${appId}/public/data/expenseReports/${initialData.id}/expenseItems`);
                const snapshot = await getDocs(itemsRef);
                const existingItems = snapshot.docs.map(doc => ({
                    ...doc.data(),
                    id: doc.id,
                    expenseDate: doc.data().expenseDate.toDate().toISOString().split('T')[0],
                }));
                setItems(existingItems);
            };
            fetchItems();
        }
    }, [initialData, isEditMode, appId]);

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                if (!response.ok) throw new Error('Failed to fetch exchange rates.');
                const data = await response.json();
                setExchangeRates(data.rates);
            } catch (err) {
                console.error(err);
                showModal("API Error", "Could not load currency conversion rates.");
            } finally {
                setRatesLoading(false);
            }
        };
        fetchRates();
    }, [showModal]);

    const reimbursementCurrency = useMemo(() => {
        if (entity === 'India') return 'INR';
        if (entity === 'Singapore') return 'SGD';
        return null;
    }, [entity]);

    const totalReimbursableAmount = useMemo(() => {
        return items.reduce((sum, item) => sum + (parseFloat(item.reimbursableAmount) || 0), 0).toFixed(2);
    }, [items]);

    const handleAddItem = () => {
        let defaultCurrency = 'INR';
        if (entity === 'India') defaultCurrency = 'INR';
        else if (entity === 'Singapore') defaultCurrency = 'SGD';
        setItems([...items, {
            id: Date.now(),
            expenseDate: '', category: '', vendor: '',
            description: '', amount: '', currency: defaultCurrency,
            reimbursableAmount: 0, isOverridden: false,
            receiptFile: null, isScanning: false,
        }]);
    };

    const handleRemoveItem = (id) => {
        if (typeof id === 'string') {
            setDeletedItemIds(prev => [...prev, id]);
        }
        setItems(items.filter(item => item.id !== id));
    };

    const handleItemChange = (id, field, value) => {
        setItems(currentItems => currentItems.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'reimbursableAmount') {
                    updatedItem.isOverridden = true;
                } 
                else if ((field === 'amount' || field === 'currency') && exchangeRates && reimbursementCurrency) {
                    updatedItem.isOverridden = false;
                    const amount = parseFloat(updatedItem.amount) || 0;
                    const fromCurrency = updatedItem.currency;
                    let newReimbursableAmount = 0;
                    if (fromCurrency === reimbursementCurrency) {
                        newReimbursableAmount = amount;
                    } else if (exchangeRates[fromCurrency] && exchangeRates[reimbursementCurrency]) {
                        const amountInUSD = amount / exchangeRates[fromCurrency];
                        newReimbursableAmount = amountInUSD * exchangeRates[reimbursementCurrency];
                    }
                    updatedItem.reimbursableAmount = newReimbursableAmount.toFixed(2);
                }
                return updatedItem;
            }
            return item;
        }));
    };
    
    const handleScanReceipt = async (id, file) => {
    if (!file) return;
    setItems(prev => prev.map(item => item.id === id ? { ...item, isScanning: true, receiptFile: file } : item));
    
    try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise(resolve => reader.onload = resolve);
        const base64Data = reader.result.split(',')[1];

        // --- NEW FIREBASE CALLABLE LOGIC ---
        const functions = getFunctions();
        const scanReceiptFn = httpsCallable(functions, 'scanReceipt');
        
        const result = await scanReceiptFn({ 
            image: base64Data, 
            mimeType: file.type 
        });
        
        // The text is returned inside result.data.text based on our backend update
        const rawText = result.data.text; 

        // --- MODIFIED: Clean the string to remove markdown formatting ---
        const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        const data = JSON.parse(jsonString);

        setItems(prev => prev.map(item => {
            if (item.id === id) {
                const updatedItem = {
                    ...item, 
                    vendor: data.vendor || '', 
                    amount: data.totalAmount || '',
                    currency: data.currency || item.currency,
                    expenseDate: data.date || '',
                    isScanning: false,
                    isOverridden: false, 
                };

                if (exchangeRates && reimbursementCurrency) {
                    const amount = parseFloat(updatedItem.amount) || 0;
                    const fromCurrency = updatedItem.currency;
                    let newReimbursableAmount = 0;

                    if (fromCurrency === reimbursementCurrency) {
                        newReimbursableAmount = amount;
                    } else if (exchangeRates[fromCurrency] && exchangeRates[reimbursementCurrency]) {
                        const amountInUSD = amount / exchangeRates[fromCurrency];
                        newReimbursableAmount = amountInUSD * exchangeRates[reimbursementCurrency];
                    }
                    updatedItem.reimbursableAmount = newReimbursableAmount.toFixed(2);
                }
                
                return updatedItem;
            }
            return item;
        }));
    } catch (err) {
        console.error("Error scanning receipt:", err);
        showModal("AI Scan Failed", "Receipt is uploaded but please enter details manually.");
        setItems(prev => prev.map(item => item.id === id ? { ...item, isScanning: false } : item));
    }
};
    
    const handleSubmit = async () => {
        setIsSubmitting(true);
        if (!reportName || !entity || items.length === 0) {
            showModal("Missing Information", "Please provide a Report Name, Billing Entity, and at least one expense item.");
            setIsSubmitting(false);
            return;
        }
        for (const [index, item] of items.entries()) {
            if (!item.expenseDate || !item.category || !item.description || !item.amount || !item.currency) {
                showModal("Incomplete Item", `Please fill all required fields for expense item #${index + 1}. Vendor name is optional.`);
                setIsSubmitting(false);
                return;
            }
        }
        const itemsWithoutReceipts = items.some(item => !item.receiptFile);
        if (itemsWithoutReceipts) {
            const proceed = window.confirm("Warning: Some expense items are missing receipts. Reports without receipts may be rejected. Do you want to proceed anyway?");
            if (!proceed) {
                setIsSubmitting(false);
                return;
            }
        }
        try {
            const reportRef = isEditMode 
                ? doc(db, `/artifacts/${appId}/public/data/expenseReports`, initialData.id)
                : doc(collection(db, `/artifacts/${appId}/public/data/expenseReports`));
            const reportData = {
                reportName, entity,
                totalAmount: parseFloat(totalReimbursableAmount),
                reimbursementCurrency: reimbursementCurrency,
                requesterId: userProfile.uid,
                requesterName: userProfile.name,
                department: userProfile.department,
                managerId: userProfile.managerId,
                status: 'Pending Approval',
                updatedAt: new Date(),
            };
            const batch = writeBatch(db);
            if (isEditMode) {
                batch.update(reportRef, reportData);
            } else {
                reportData.reportNumber = generateReportNumber();
                reportData.createdAt = new Date();
                batch.set(reportRef, reportData);
            }
            for (const item of items) {
                let receiptInfo = item.receiptFile;
                if (receiptInfo instanceof File) {
                    const filePath = `receipts/${appId}/${userProfile.uid}/${Date.now()}-${receiptInfo.name}`;
                    const fileRef = ref(storage, filePath);
                    await uploadBytes(fileRef, receiptInfo);
                    const downloadURL = await getDownloadURL(fileRef);
                    receiptInfo = { name: receiptInfo.name, url: downloadURL, path: filePath };
                }
                const itemRef = typeof item.id === 'string' 
                    ? doc(db, `/artifacts/${appId}/public/data/expenseReports/${reportRef.id}/expenseItems`, item.id)
                    : doc(collection(reportRef, 'expenseItems'));
                const originalAmount = parseFloat(item.amount) || 0;
                const reimbursable = parseFloat(item.reimbursableAmount) || 0;
                let effectiveRate = null;
                if (originalAmount > 0 && reimbursable > 0 && item.currency !== reimbursementCurrency) {
                    effectiveRate = (reimbursable / originalAmount);
                }
                batch.set(itemRef, {
                    expenseDate: new Date(item.expenseDate),
                    category: item.category,
                    vendor: item.vendor,
                    description: item.description,
                    amount: originalAmount,
                    currency: item.currency,
                    reimbursableAmount: reimbursable,
                    effectiveConversionRate: effectiveRate,
                    isOverridden: item.isOverridden,
                    receiptFile: receiptInfo,
                }, { merge: true });
            }
            for (const itemIdToDelete of deletedItemIds) {
                const itemRefToDelete = doc(db, `/artifacts/${appId}/public/data/expenseReports/${reportRef.id}/expenseItems`, itemIdToDelete);
                batch.delete(itemRefToDelete);
            }
            await batch.commit();
	    /*
            if (userProfile.managerId) {
                try {
                    const managerRef = doc(db, `/artifacts/${appId}/users`, userProfile.managerId);
                    const managerDoc = await getDoc(managerRef);
                    if (managerDoc.exists()) {
                        const managerEmail = managerDoc.data().email;
                        const emailContent = newExpenseReportTemplate(reportData, userProfile.name);
                        await sendNotificationEmail({
                            to: [managerEmail],
                            ...emailContent,
                            appId: appId,
                        });
                    }
                } catch (emailError) {
                    console.error("Submission successful, but failed to send notification:", emailError);
                }
            }
            */
            showModal("Success", `Your report has been ${isEditMode ? 'updated and resubmitted' : 'submitted'}.`);
            onCancel();
        } catch (err) {
            console.error("Error submitting report:", err);
            showModal("Error", "There was an error submitting your report.");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // --- MODIFIED: Logic to display the correct rejection reason ---
    const rejectionReason = initialData?.approvalComments || initialData?.financeRejectionReason;
    const reasonLabel = initialData?.financeRejectionReason ? "Finance Team's Comments" : "Manager's Comments";

    return (
        <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-[#213368] mb-6">
                {isEditMode ? `Edit Expense Report: ${initialData?.reportNumber}` : 'New Expense Report'}
            </h2>
            
            {/* --- MODIFIED: This block now displays either rejection reason --- */}
            {isEditMode && rejectionReason && (
                <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded-md text-sm">
                    <strong>{reasonLabel}:</strong> {rejectionReason}
                </div>
            )}

            <div className="bg-white p-8 rounded-lg shadow-md space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Report Name</label>
                        <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" placeholder="e.g., Q4 Client Trip to Melbourne" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Entity</label>
                        <select value={entity} onChange={e => setEntity(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white">
                            <option value="">Select entity...</option>
                            <option value="Singapore">Singapore</option>
                            <option value="India">India</option>
                        </select>
                    </div>
                </div>

                <div className="border-t pt-6">
                    <h3 className="text-lg font-medium text-[#213368] mb-4">Expenses</h3>
                    {items.length > 0 && (
                        <div className="space-y-4">
                            {items.map(item => (
                                <ExpenseItemRow 
                                    key={item.id} item={item} categories={categories}
                                    onChange={handleItemChange} onRemove={handleRemoveItem}
                                    onScan={handleScanReceipt} reimbursementCurrency={reimbursementCurrency}
                                />
                            ))}
                        </div>
                    )}
                    <button onClick={handleAddItem} className="mt-4 px-4 py-2 text-sm bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300">
                        + Add Expense Item
                    </button>
                </div>

                <div className="border-t pt-6 flex justify-between items-center">
                    <div>
                        <span className="text-lg font-bold text-gray-800">Total Reimbursable:</span>
                        <span className="ml-2 text-xl font-bold text-[#213368]">
                            {totalReimbursableAmount} {reimbursementCurrency || ''}
                        </span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button onClick={onCancel} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                        <button onClick={handleSubmit} disabled={isSubmitting || ratesLoading || categoriesLoading} className="px-6 py-2 bg-[#20BEC6] text-white font-medium rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400">
                            {isSubmitting ? (isEditMode ? 'Resubmitting...' : 'Submitting...') : (isEditMode ? 'Resubmit for Approval' : 'Submit for Approval')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ExpenseItemRow({ item, categories, onChange, onRemove, onScan, reimbursementCurrency }) {
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) onScan(item.id, file);
    };
    
    const effectiveRate = useMemo(() => {
        const originalAmount = parseFloat(item.amount) || 0;
        const reimbursable = parseFloat(item.reimbursableAmount) || 0;
        if (originalAmount === 0 || reimbursable === 0 || item.currency === reimbursementCurrency) {
            return null;
        }
        return (reimbursable / originalAmount).toFixed(4);
    }, [item.amount, item.reimbursableAmount, item.currency, reimbursementCurrency]);

    return (
        <div className="p-4 border rounded-lg bg-gray-50 relative">
            {item.isScanning && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center rounded-lg z-10">
                    <LoadingSpinner /> <span className="ml-2 text-sm">Scanning...</span>
                </div>
            )}
            <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-1 flex items-center justify-center">
                    <label className="cursor-pointer text-gray-500 hover:text-blue-600" title="Upload or Scan Receipt">
                        <UploadIcon />
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileSelect} />
                    </label>
                </div>

                <div className="col-span-10 grid grid-cols-1 md:grid-cols-6 gap-4">
                    <input type="date" value={item.expenseDate} onChange={e => onChange(item.id, 'expenseDate', e.target.value)} className="w-full p-2 border rounded-md" />
                    <select value={item.category} onChange={e => onChange(item.id, 'category', e.target.value)} className="w-full p-2 border rounded-md bg-white">
                        <option value="">Category...</option>
                        {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                    </select>
                    <input type="text" value={item.vendor} onChange={e => onChange(item.id, 'vendor', e.target.value)} placeholder="Vendor" className="w-full p-2 border rounded-md md:col-span-2" />
                    
                    <div className="flex items-center col-span-2">
                        <input type="number" value={item.amount} onChange={e => onChange(item.id, 'amount', e.target.value)} placeholder="Amount" className="w-2/3 p-2 border rounded-l-md" />
                        <select value={item.currency} onChange={e => onChange(item.id, 'currency', e.target.value)} className="w-1/3 p-2 border-t border-b border-r rounded-r-md bg-gray-100">
                            {allCurrencies.map(c => <option key={c}>{c}</option>)}
                        </select>
                    </div>

                    <div className="flex items-center col-span-2">
                         <input type="number" value={item.reimbursableAmount} onChange={e => onChange(item.id, 'reimbursableAmount', e.target.value)} placeholder="Reimbursable" className="w-2/3 p-2 border rounded-l-md bg-white" />
                         <span className="w-1/3 p-2 border-t border-b border-r rounded-r-md bg-gray-100 text-sm text-gray-600">{reimbursementCurrency || '...'}</span>
                    </div>
                    
                    <textarea value={item.description} onChange={e => onChange(item.id, 'description', e.target.value)} placeholder="Description..." rows="1" className="w-full md:col-span-6 p-2 border rounded-md"></textarea>
                </div>
                
                <div className="col-span-1 flex items-center justify-center">
                    <button onClick={() => onRemove(item.id)} className="text-red-500 hover:text-red-700" title="Remove Item"><TrashIcon /></button>
                </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-2 flex justify-center items-center space-x-4">
                {effectiveRate && <span>Effective Rate: 1 {item.currency} = {effectiveRate} {reimbursementCurrency}</span>}
                {item.isOverridden && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">Manual Override</span>}
            </div>

            {item.receiptFile && <p className="text-xs text-green-600 mt-1 text-center">Receipt: {item.receiptFile.name || "Attached"}</p>}
        </div>
    );
}